<script setup lang="ts">
import { watch } from "vue";

import { TSiteSection } from "@/data/api/kili/sites/types/site-sections.type";
import { useTypedIntl } from "@/locales/typed-intl";
import { EMixpanelEvent } from "@/services/mixpanel/mixpanel-events.type";
import { MixpanelService } from "@/services/mixpanel/mixpanel-service";
import { capitalizeWords } from "@/utils/string-utils";

import SectionAppliancesList from "@/app/tools/section-management/site-sections/section-appliances-list.vue";
import HospitalityButton from "@/components/ui/buttons/hospitality-button.vue";
import BaseDialog from "@/components/ui/dialog/base-dialog.vue";

interface IProps {
  siteId: number;
  section: TSiteSection;
}

const visible = defineModel<boolean>();
const props = defineProps<IProps>();

const { t, k } = useTypedIntl();

watch(
  visible,
  (newValue) => {
    if (newValue) {
      MixpanelService.trackEvent(EMixpanelEvent.SECTIONS_MANAGEMENT_APPLIANCES_LIST);
    }
  },
  { immediate: true },
);
</script>

<template>
  <BaseDialog
    v-model:visible="visible"
    class="section-appliances-dialog"
    modal
    size="small"
  >
    <template #header>
      <div class="flex flex-col gap-2">
        <h4 class="font-semibold">
          {{ t(k.tools.siteManagement.siteSections.sectionAppliances.header) }}
        </h4>

        <div class="flex flex-row row-dot-divider">
          <span>{{ capitalizeWords(t(k.global.section)) }}</span>
          <span>{{ props.section.name ?? "-" }}</span>
        </div>
      </div>
    </template>

    <div class="h-full flex">
      <SectionAppliancesList
        :section-id="props.section.id"
        :site-id="props.siteId"
      />
    </div>

    <template #footer>
      <HospitalityButton
        class="w-full"
        data-test-id="section-appliances-close-button"
        :label="t(k.global.misc.close)"
        outlined
        severity="secondary"
        @click="visible = false"
      />
    </template>
  </BaseDialog>
</template>

<style lang="scss">
.section-appliances-dialog {
  .p-dialog-content {
    @apply pb-0;
  }
}
</style>
